import { useEffect, useState, useRef } from 'react';
import { Loader2, Save, Percent, Star, Upload, X, Eye, EyeOff, Megaphone, FileText, ShoppingCart, Bell, Clock, CreditCard, Landmark, Instagram, MessageCircle, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { adminAPI } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import { DangerZone } from '@/components/admin/DangerZone';
import { AutoCloseReadySettings } from '@/components/admin/AutoCloseReadySettings';
import { money } from '@/lib/format';

const EMAIL_LABELS = {
  payment_requested: 'Payment requested (invoice / payment link)',
  payment_received: 'Payment received',
  confirmed: 'Order confirmed',
  packing: 'Packing started',
  ready_for_collection: 'Ready for collection',
  closed: 'Order closed',
  rejected: 'Order rejected',
  refund_issued: 'Item refunded (out-of-stock)',
  unpaid_reminder: 'Unpaid payment reminders',
};
const EMAIL_DEFAULTS = {
  payment_requested: true, payment_received: true, confirmed: true,
  packing: false, ready_for_collection: true, closed: false,
  rejected: true, refund_issued: true, unpaid_reminder: true,
};

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [markup, setMarkup] = useState('');
  const [featuredVisible, setFeaturedVisible] = useState(true);
  const [featuredIds, setFeaturedIds] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [bannerText, setBannerText] = useState('');
  const [bannerVisible, setBannerVisible] = useState(false);
  const [windowBarVisible, setWindowBarVisible] = useState(true);
  const [savingBanner, setSavingBanner] = useState(false);
  const [invoice, setInvoice] = useState({
    company_name: '', registration_number: '', vat_number: '',
    address: '', address_line1: '', address_line2: '', suburb: '', city: '', postal_code: '',
    bank_details: '', phone: '', email: ''
  });
  const [savingInvoice, setSavingInvoice] = useState(false);
  const [multiSupplier, setMultiSupplier] = useState(false);
  const [emailPrefs, setEmailPrefs] = useState({});
  const [reminderHours, setReminderHours] = useState('');
  const [reminderBeforeClose, setReminderBeforeClose] = useState(true);
  const [reminderAfterPlaced, setReminderAfterPlaced] = useState(false);
  const [reminderAfterHours, setReminderAfterHours] = useState('');
  const [paymentDueHours, setPaymentDueHours] = useState('48');
  const [reminderAfterRequestHours, setReminderAfterRequestHours] = useState('');
  const [savingReminders, setSavingReminders] = useState(false);
  const [vipMarkup, setVipMarkup] = useState('');
  const [savingVip, setSavingVip] = useState(false);
  const [tithe, setTithe] = useState('');
  const [savingTithe, setSavingTithe] = useState(false);
  const [vatRate, setVatRate] = useState('15');
  const [savingVat, setSavingVat] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState({ card: true, eft: false });
  const [autoCancelEnabled, setAutoCancelEnabled] = useState(true);
  const [autoCancelGrace, setAutoCancelGrace] = useState('2');
  const [savingAutoCancel, setSavingAutoCancel] = useState(false);
  const [windowHeadsUpEnabled, setWindowHeadsUpEnabled] = useState(false);
  const [cartReminderEnabled, setCartReminderEnabled] = useState(false);
  const [cartReminderLeadHours, setCartReminderLeadHours] = useState('24');
  const [cartReminderEmail, setCartReminderEmail] = useState(true);
  const [cartReminderWhatsapp, setCartReminderWhatsapp] = useState(true);
  const [windowLeadHours, setWindowLeadHours] = useState('72');
  const [social, setSocial] = useState({ instagram_url: '' });
  const [waContact, setWaContact] = useState({ enabled: false, number: '', message: '' });
  const [savingSocial, setSavingSocial] = useState(false);

  useEffect(() => {
    Promise.all([adminAPI.getSettings(), adminAPI.listProducts()])
      .then(([settingsRes, productsRes]) => {
        const s = settingsRes.data;
        setSettings(s);
        setMarkup(String(s.markup_percentage));
        setVipMarkup(String(s.vip_markup_percentage ?? 0));
        setTithe(String(s.tithe_percentage ?? 10));
        setVatRate(String(s.vat_rate ?? 15));
        setFeaturedVisible(s.featured_section_visible !== false);
        setFeaturedIds(s.featured_product_ids || []);
        setBannerText(s.banner_text || '');
        setBannerVisible(s.banner_visible || false);
        setWindowBarVisible(s.window_bar_visible !== false);
        setMultiSupplier(!!s.allow_multi_supplier_carts);
        setEmailPrefs(s.email_notifications || {});
        setReminderHours((s.unpaid_reminder_hours_before_close || [72, 24, 6]).join(', '));
        setReminderBeforeClose(s.reminder_before_close_enabled !== false);
        setReminderAfterPlaced(s.reminder_after_placed_enabled === true);
        setReminderAfterHours((s.reminder_after_placed_hours || [24, 72]).join(', '));
        setPaymentDueHours(String(s.payment_due_hours ?? 48));
        setReminderAfterRequestHours((s.reminder_hours_after_request || [24, 6]).join(', '));
        setWindowHeadsUpEnabled(s.window_opening_heads_up_enabled === true);
        setCartReminderEnabled(s.cart_reminder_enabled === true);
        setCartReminderLeadHours(String(s.cart_reminder_lead_hours ?? 24));
        setCartReminderEmail(s.cart_reminder_email_enabled !== false);
        setCartReminderWhatsapp(s.cart_reminder_whatsapp_enabled !== false);
        setWindowLeadHours(String(s.window_opening_lead_hours ?? 72));
        setPaymentMethods({ card: true, eft: false, ...(s.payment_methods || {}) });
        setAutoCancelEnabled(s.auto_cancel_unpaid_enabled !== false);
        setAutoCancelGrace(String(s.auto_cancel_grace_hours ?? 2));
        setSocial({ instagram_url: s.instagram_url || '' });
        setWaContact({
          enabled: !!s.whatsapp_contact_enabled,
          number: s.whatsapp_contact_number || '',
          message: s.whatsapp_contact_message || '',
        });
        setInvoice({
          company_name: s.invoice_company_name || '',
          registration_number: s.invoice_registration_number || '',
          vat_number: s.invoice_vat_number || '',
          address: s.invoice_address || '',
          address_line1: s.invoice_address_line1 || '',
          address_line2: s.invoice_address_line2 || '',
          suburb: s.invoice_suburb || '',
          city: s.invoice_city || '',
          postal_code: s.invoice_postal_code || '',
          bank_details: s.invoice_bank_details || '',
          phone: s.invoice_phone || '',
          email: s.invoice_email || ''
        });
        setAllProducts(productsRes.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSaveMarkup = async () => {
    const val = parseFloat(markup);
    if (isNaN(val) || val < 0 || val > 200) {
      toast.error('Markup must be between 0% and 200%');
      return;
    }
    setSaving(true);
    try {
      await adminAPI.updateSettings({ markup_percentage: val });
      toast.success(`Markup updated to ${val}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveVipMarkup = async () => {
    const val = parseFloat(vipMarkup);
    if (isNaN(val) || val < 0 || val > 200) {
      toast.error('VIP markup must be between 0% and 200%');
      return;
    }
    setSavingVip(true);
    try {
      await adminAPI.updateSettings({ vip_markup_percentage: val });
      toast.success(val === 0 ? 'VIPs now pay cost price' : `VIP markup updated to ${val}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingVip(false);
    }
  };

  const handleSaveTithe = async () => {
    const val = parseFloat(tithe);
    if (isNaN(val) || val < 0 || val > 100) {
      toast.error('Tithe must be between 0% and 100%');
      return;
    }
    setSavingTithe(true);
    try {
      await adminAPI.updateSettings({ tithe_percentage: val });
      toast.success(`Tithe updated to ${val}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingTithe(false);
    }
  };

  const handleSaveVat = async () => {
    const val = parseFloat(vatRate);
    if (isNaN(val) || val < 0 || val > 100) {
      toast.error('VAT rate must be between 0% and 100%');
      return;
    }
    setSavingVat(true);
    try {
      await adminAPI.updateSettings({ vat_rate: val });
      toast.success(`VAT rate updated to ${val}%`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingVat(false);
    }
  };


  const handleToggleFeaturedVisibility = async (visible) => {
    setFeaturedVisible(visible);
    try {
      await adminAPI.updateSettings({ featured_section_visible: visible });
      toast.success(visible ? 'Featured section shown' : 'Featured section hidden');
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleToggleMultiSupplier = async (allow) => {
    setMultiSupplier(allow);
    try {
      await adminAPI.updateSettings({ allow_multi_supplier_carts: allow });
      toast.success(allow
        ? 'Customers can now mix items from multiple suppliers'
        : 'Customers must check out one supplier at a time'
      );
    } catch {
      toast.error('Failed to update');
      setMultiSupplier(!allow);
    }
  };

  const handleTogglePaymentMethod = async (key, val) => {
    // Guard: must keep at least one method on.
    const next = { ...paymentMethods, [key]: val };
    if (!next.card && !next.eft) {
      toast.error('At least one payment method must remain enabled');
      return;
    }
    const prev = paymentMethods;
    setPaymentMethods(next);
    try {
      const res = await adminAPI.updateSettings({ payment_methods: next });
      setPaymentMethods({ card: true, eft: false, ...(res.data?.payment_methods || next) });
      toast.success(`${key === 'card' ? 'Card payments' : 'EFT'}: ${val ? 'on' : 'off'}`);
    } catch {
      toast.error('Failed to update');
      setPaymentMethods(prev);
    }
  };

  const toggleEmail = async (key, val) => {
    const prev = emailPrefs;
    setEmailPrefs(p => ({ ...p, [key]: val }));
    try {
      await adminAPI.updateSettings({ email_notifications: { [key]: val } });
      toast.success(`${EMAIL_LABELS[key] || key}: ${val ? 'on' : 'off'}`);
    } catch {
      toast.error('Failed to update');
      setEmailPrefs(prev);
    }
  };

  const saveReminders = async () => {
    const parsed = reminderHours
      .split(',')
      .map(s => parseInt(s.trim(), 10))
      .filter(n => Number.isFinite(n) && n > 0);
    const parsedAfter = reminderAfterHours
      .split(',')
      .map(s => parseInt(s.trim(), 10))
      .filter(n => Number.isFinite(n) && n > 0);
    if (reminderBeforeClose && parsed.length === 0) {
      toast.error('Enter at least one positive "before close" hour value');
      return;
    }
    if (reminderAfterPlaced && parsedAfter.length === 0) {
      toast.error('Enter at least one positive "after placed" hour value');
      return;
    }
    const parsedAfterRequest = reminderAfterRequestHours
      .split(',')
      .map(s => parseInt(s.trim(), 10))
      .filter(n => Number.isFinite(n) && n > 0);
    setSavingReminders(true);
    try {
      const res = await adminAPI.updateSettings({
        unpaid_reminder_hours_before_close: parsed,
        reminder_before_close_enabled: reminderBeforeClose,
        reminder_after_placed_enabled: reminderAfterPlaced,
        reminder_after_placed_hours: parsedAfter,
        payment_due_hours: Math.min(720, Math.max(1, parseInt(paymentDueHours, 10) || 48)),
        reminder_hours_after_request: parsedAfterRequest.length ? parsedAfterRequest : [24, 6],
        window_opening_heads_up_enabled: windowHeadsUpEnabled,
        window_opening_lead_hours: Math.max(1, parseInt(windowLeadHours, 10) || 72),
        cart_reminder_enabled: cartReminderEnabled,
        cart_reminder_lead_hours: Math.min(168, Math.max(1, parseInt(cartReminderLeadHours, 10) || 24)),
        cart_reminder_email_enabled: cartReminderEmail,
        cart_reminder_whatsapp_enabled: cartReminderWhatsapp,
      });
      setReminderHours((res.data.unpaid_reminder_hours_before_close || parsed).join(', '));
      setReminderAfterHours((res.data.reminder_after_placed_hours || parsedAfter).join(', '));
      toast.success('Reminder settings saved');
    } catch {
      toast.error('Failed to save reminders');
    } finally {
      setSavingReminders(false);
    }
  };

  const handleToggleFeaturedProduct = async (productId) => {
    const newIds = featuredIds.includes(productId)
      ? featuredIds.filter(id => id !== productId)
      : [...featuredIds, productId];
    setFeaturedIds(newIds);
    try {
      await adminAPI.updateSettings({ featured_product_ids: newIds });
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleImageUpload = async (productId, file) => {
    setUploading(productId);
    try {
      const res = await adminAPI.uploadImage(file);
      await adminAPI.updateProduct(productId, { image_url: res.data.url });
      setAllProducts(prev => prev.map(p => p.id === productId ? { ...p, image_url: res.data.url } : p));
      toast.success('Image uploaded');
    } catch {
      toast.error('Failed to upload');
    } finally {
      setUploading(null);
    }
  };

  if (loading) {
    return (
      <div data-testid="admin-settings-loading" className="space-y-6">
        <div className="h-8 w-48 bg-muted rounded animate-pulse" />
        <div className="h-40 bg-muted rounded-2xl animate-pulse" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings" className="space-y-8 max-w-4xl">
      <div className="mb-2">
        <h1 className="font-heading text-3xl font-light text-foreground tracking-tight">Settings</h1>
        <p className="font-body text-sm text-foreground/70 mt-1">Configure global platform settings</p>
      </div>

      {/* Markup Configuration */}
      <div className="p-8 rounded-2xl bg-card border border-border">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-xl bg-vintage/10">
            <Percent className="h-5 w-5 text-vintage" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-medium text-foreground">Price Markup</h2>
            <p className="font-body text-xs text-foreground/60">Applied to all product cost prices for non-VIP customers</p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="font-body text-sm text-foreground font-medium">Markup Percentage (%)</Label>
            <div className="flex items-center gap-3 mt-2">
              <Input
                data-testid="markup-input"
                type="number"
                step="0.5"
                min="0"
                max="200"
                value={markup}
                onChange={e => setMarkup(e.target.value)}
                className="rounded-xl bg-card border-border font-body max-w-xs"
              />
              <span className="font-body text-lg text-foreground font-bold">%</span>
            </div>
            <p className="font-body text-xs text-foreground/50 mt-2">
              Example: R100 cost at {markup}% markup = R{money((100 * (1 + parseFloat(markup || 0) / 100)))} sell price
            </p>
          </div>

          <div className="pt-4 border-t border-border">
            <Button
              onClick={handleSaveMarkup}
              disabled={saving}
              data-testid="save-settings-btn"
              className="rounded-full bg-teal-dark border border-transparent text-white hover:bg-teal font-body text-sm gap-2"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save Markup
            </Button>
          </div>

          {/* VIP markup */}
          <div className="pt-4 border-t border-border" data-testid="vip-markup-section">
            <div className="flex items-center gap-2 mb-2">
              <Star className="h-4 w-4 text-gold" strokeWidth={2} />
              <Label className="font-body text-sm text-foreground font-medium">VIP Markup Percentage (%)</Label>
            </div>
            <p className="font-body text-xs text-foreground/55 mb-3">
              Applied only to customers flagged as VIP. Leave at 0% for cost-price VIP pricing.
            </p>
            <div className="flex items-center gap-3">
              <Input
                data-testid="vip-markup-input"
                type="number"
                step="0.5"
                min="0"
                max="200"
                value={vipMarkup}
                onChange={e => setVipMarkup(e.target.value)}
                className="rounded-xl bg-card border-border font-body max-w-xs"
              />
              <span className="font-body text-lg text-foreground font-bold">%</span>
              <Button
                onClick={handleSaveVipMarkup}
                disabled={savingVip}
                data-testid="save-vip-markup-btn"
                className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2"
              >
                {savingVip ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save VIP
              </Button>
            </div>
            <p className="font-body text-xs text-foreground/50 mt-2">
              Example: R100 cost at {vipMarkup || 0}% VIP markup = R{money((100 * (1 + parseFloat(vipMarkup || 0) / 100)))} VIP sell price
            </p>
          </div>

          {/* Tithe percentage — applied to goods-level profit in Reports */}
          <div className="pt-4 border-t border-border" data-testid="tithe-section">
            <div className="flex items-center gap-2 mb-2">
              <Percent className="h-4 w-4 text-teal" strokeWidth={2} />
              <Label className="font-body text-sm text-foreground font-medium">Tithe Percentage (%)</Label>
            </div>
            <p className="font-body text-xs text-foreground/55 mb-3">
              Applied to goods-level profit (Sales − Cost) in the <span className="font-semibold">Profit Margin by Supplier</span> report. Shown as a Tithe column and grand total.
            </p>
            <div className="flex items-center gap-3">
              <Input
                data-testid="tithe-input"
                type="number"
                step="0.5"
                min="0"
                max="100"
                value={tithe}
                onChange={e => setTithe(e.target.value)}
                className="rounded-xl bg-card border-border font-body max-w-xs"
              />
              <span className="font-body text-lg text-foreground font-bold">%</span>
              <Button
                onClick={handleSaveTithe}
                disabled={savingTithe}
                data-testid="save-tithe-btn"
                className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2"
              >
                {savingTithe ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Tithe
              </Button>
            </div>
            <p className="font-body text-xs text-foreground/50 mt-2">
              Example: R1,000 profit at {tithe || 0}% tithe = R{money((1000 * (parseFloat(tithe || 0) / 100)))}
            </p>
          </div>

          {/* VAT rate — used to compute VAT-exclusive product costs */}
          <div className="pt-4 border-t border-border" data-testid="vat-section">
            <div className="flex items-center gap-2 mb-2">
              <Percent className="h-4 w-4 text-teal" strokeWidth={2} />
              <Label className="font-body text-sm text-foreground font-medium">VAT Rate (%)</Label>
            </div>
            <p className="font-body text-xs text-foreground/55 mb-3">
              Used to compute the <span className="font-semibold">VAT-exclusive cost</span> of products belonging to VAT-vendor suppliers. Changing this recomputes ex-VAT costs across all VATable products. South Africa standard = 15%.
            </p>
            <div className="flex items-center gap-3">
              <Input
                data-testid="vat-rate-input"
                type="number"
                step="0.5"
                min="0"
                max="100"
                value={vatRate}
                onChange={e => setVatRate(e.target.value)}
                className="rounded-xl bg-card border-border font-body max-w-xs"
              />
              <span className="font-body text-lg text-foreground font-bold">%</span>
              <Button
                onClick={handleSaveVat}
                disabled={savingVat}
                data-testid="save-vat-btn"
                className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2"
              >
                {savingVat ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save VAT Rate
              </Button>
            </div>
            <p className="font-body text-xs text-foreground/50 mt-2">
              Example: R115.00 VAT-inclusive cost at {vatRate || 0}% = R{money((parseFloat(vatRate) > 0 ? 115 / (1 + parseFloat(vatRate) / 100) : 115))} ex VAT
            </p>
          </div>
        </div>
      </div>

      {/* Cart Behaviour */}
      <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border" data-testid="cart-behaviour-card">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-teal/10">
              <ShoppingCart className="h-5 w-5 text-teal" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-foreground">Cart Behaviour</h2>
              <p className="font-body text-xs text-foreground/60">Controls how customers can mix suppliers in a single order.</p>
            </div>
          </div>
        </div>
        <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-muted/30 border border-border/60">
          <div className="min-w-0">
            <p className="font-body text-sm text-foreground font-semibold mb-1">Allow multi-supplier carts</p>
            <p className="font-body text-xs text-foreground/60 leading-relaxed">
              When <span className="font-semibold text-foreground/80">off</span>, customers can only buy from one supplier at a time
              — trying to add a second supplier&apos;s item prompts them to replace their cart.
              Turn this <span className="font-semibold text-foreground/80">on</span> to let customers combine items from multiple
              suppliers into one order.
            </p>
          </div>
          <Switch
            data-testid="multi-supplier-toggle"
            checked={multiSupplier}
            onCheckedChange={handleToggleMultiSupplier}
          />
        </div>
      </div>

      {/* Payment Methods */}
      <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border" data-testid="payment-methods-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-xl bg-gold/10">
            <CreditCard className="h-5 w-5 text-gold" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-medium text-foreground">Payment Methods</h2>
            <p className="font-body text-xs text-foreground/60">
              Decide which methods customers see at checkout. EFT requires you to mark received payments manually under Orders.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-muted/30 border border-border/60">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <CreditCard className="h-4 w-4 text-teal" strokeWidth={2} />
                <p className="font-body text-sm text-foreground font-semibold">Card (Paystack)</p>
              </div>
              <p className="font-body text-xs text-foreground/60 leading-relaxed">
                Hosted checkout — instant capture and webhook confirmation. Requires Paystack keys.
              </p>
            </div>
            <Switch
              data-testid="payment-method-card-toggle"
              checked={!!paymentMethods.card}
              onCheckedChange={(v) => handleTogglePaymentMethod('card', v)}
            />
          </div>
          <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-muted/30 border border-border/60">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Landmark className="h-4 w-4 text-gold" strokeWidth={2} />
                <p className="font-body text-sm text-foreground font-semibold">EFT (Manual bank transfer)</p>
              </div>
              <p className="font-body text-xs text-foreground/60 leading-relaxed">
                Customers see banking details + reference at checkout. Mark as paid under Orders once funds reflect.
              </p>
            </div>
            <Switch
              data-testid="payment-method-eft-toggle"
              checked={!!paymentMethods.eft}
              onCheckedChange={(v) => handleTogglePaymentMethod('eft', v)}
            />
          </div>
        </div>
        {paymentMethods.eft && !invoice.bank_details && (
          <p
            data-testid="eft-bank-details-warning"
            className="mt-3 font-body text-xs text-gold/80 bg-gold/10 border border-gold/30 rounded-lg p-3"
          >
            EFT is on, but no banking details are saved yet. Add them in the &quot;Invoice &amp; Business Details&quot; section below — they appear on the customer&apos;s EFT instructions email and the checkout page.
          </p>
        )}
      </div>

      {/* Customer Email Notifications */}
      <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border" data-testid="email-notifications-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-xl bg-teal/10">
            <Bell className="h-5 w-5 text-teal" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-medium text-foreground">Customer Email Notifications</h2>
            <p className="font-body text-xs text-foreground/60">Choose which state changes trigger an email to the customer.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {Object.keys(EMAIL_LABELS).map(key => {
            const val = emailPrefs[key];
            const checked = val === undefined ? EMAIL_DEFAULTS[key] : !!val;
            return (
              <div
                key={key}
                data-testid={`email-toggle-row-${key}`}
                className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border/40"
              >
                <span className="font-body text-sm text-foreground/80">{EMAIL_LABELS[key]}</span>
                <Switch
                  data-testid={`email-toggle-${key}`}
                  checked={checked}
                  onCheckedChange={(v) => toggleEmail(key, v)}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Unpaid Reminder Schedule */}
      <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border" data-testid="reminder-cadence-card">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-xl bg-gold/10">
            <Clock className="h-5 w-5 text-gold" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-medium text-foreground">Unpaid Reminder Schedule</h2>
            <p className="font-body text-xs text-foreground/60">
              Automatic payment reminders to unpaid customers. The master "Unpaid payment reminders" email toggle above must also be on.
            </p>
          </div>
        </div>

        {/* Before window close */}
        <div className="p-4 rounded-xl bg-muted/30 border border-border/40 mb-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-body text-sm font-semibold text-foreground/90">Before window close</p>
              <p className="font-body text-xs text-foreground/60">Fire N hours before the supplier's order window closes.</p>
            </div>
            <Switch
              data-testid="reminder-before-close-toggle"
              checked={reminderBeforeClose}
              onCheckedChange={setReminderBeforeClose}
            />
          </div>
          <Label className="font-body text-xs text-foreground/60 font-medium mb-1.5 block">
            Hours before close (comma-separated)
          </Label>
          <Input
            data-testid="reminder-hours-input"
            value={reminderHours}
            onChange={(e) => setReminderHours(e.target.value)}
            placeholder="e.g. 72, 24, 6"
            disabled={!reminderBeforeClose}
            className="rounded-xl bg-muted border-border"
          />
        </div>

        {/* After order placed */}
        <div className="p-4 rounded-xl bg-muted/30 border border-border/40 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-body text-sm font-semibold text-foreground/90">After order placed</p>
              <p className="font-body text-xs text-foreground/60">Fire N hours after the order was placed.</p>
            </div>
            <Switch
              data-testid="reminder-after-placed-toggle"
              checked={reminderAfterPlaced}
              onCheckedChange={setReminderAfterPlaced}
            />
          </div>
          <Label className="font-body text-xs text-foreground/60 font-medium mb-1.5 block">
            Hours after placed (comma-separated)
          </Label>
          <Input
            data-testid="reminder-after-hours-input"
            value={reminderAfterHours}
            onChange={(e) => setReminderAfterHours(e.target.value)}
            placeholder="e.g. 24, 72"
            disabled={!reminderAfterPlaced}
            className="rounded-xl bg-muted border-border"
          />
        </div>

        {/* MOQ / stock-confirm (Type B) payment window */}
        <div className="p-4 rounded-xl bg-muted/30 border border-border/40 mb-4" data-testid="moq-payment-section">
          <p className="font-body text-sm font-semibold text-foreground/90 mb-1">Stock-confirm suppliers — pay-by window</p>
          <p className="font-body text-xs text-foreground/60 mb-3">
            For suppliers set to <strong>&quot;Pay after confirmation&quot;</strong> (MOQ). These orders are never chased or auto-cancelled while awaiting your stock confirmation. Once you confirm stock &amp; request payment, this is how long the customer has to pay, and when reminders fire. A supplier can override the hours on its own settings.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="font-body text-xs text-foreground/60 font-medium mb-1.5 block">
                Pay-by window (hours after payment requested)
              </Label>
              <Input
                data-testid="payment-due-hours-input"
                type="number"
                min={1}
                max={720}
                value={paymentDueHours}
                onChange={(e) => setPaymentDueHours(e.target.value)}
                placeholder="48"
                className="rounded-xl bg-muted border-border"
              />
            </div>
            <div>
              <Label className="font-body text-xs text-foreground/60 font-medium mb-1.5 block">
                Reminder hours before pay-by deadline (comma-separated)
              </Label>
              <Input
                data-testid="reminder-after-request-input"
                value={reminderAfterRequestHours}
                onChange={(e) => setReminderAfterRequestHours(e.target.value)}
                placeholder="e.g. 24, 6"
                className="rounded-xl bg-muted border-border"
              />
            </div>
          </div>
        </div>

        {/* Window opening soon — WhatsApp heads-up */}
        <div className="p-4 rounded-xl bg-muted/30 border border-border/40 mb-4" data-testid="window-headsup-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-body text-sm font-semibold text-foreground/90">Order window opening soon (WhatsApp)</p>
              <p className="font-body text-xs text-foreground/60">
                Master switch for the order-window WhatsApp feature. When ON: customers can opt in to alerts on their profile, and a WhatsApp is broadcast to opted-in customers before a supplier's window opens (suppliers opening the same day are combined). When OFF, the opt-in is hidden and no alerts send. Requires an approved WhatsApp template.
              </p>
            </div>
            <Switch
              data-testid="window-headsup-toggle"
              checked={windowHeadsUpEnabled}
              onCheckedChange={setWindowHeadsUpEnabled}
            />
          </div>
          <Label className="font-body text-xs text-foreground/60 font-medium mb-1.5 block">
            Lead time (hours before the window opens)
          </Label>
          <Input
            data-testid="window-headsup-lead-input"
            type="number"
            min={1}
            value={windowLeadHours}
            onChange={(e) => setWindowLeadHours(e.target.value)}
            placeholder="72"
            disabled={!windowHeadsUpEnabled}
            className="rounded-xl bg-muted border-border w-40"
          />
        </div>

        {/* Abandoned cart reminders — email &/or WhatsApp */}
        <div className="p-4 rounded-xl bg-muted/30 border border-border/40 mb-4" data-testid="cart-reminder-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="font-body text-sm font-semibold text-foreground/90">Abandoned cart reminders</p>
              <p className="font-body text-xs text-foreground/60">
                Nudge logged-in customers who still have items in their saved cart, a set number of hours before the earliest order window among those items closes. Sent during daytime only (08:00–20:00 SAST), once per cart per window cycle. Guests (not logged in) can't be reached.
              </p>
            </div>
            <Switch
              data-testid="cart-reminder-toggle"
              checked={cartReminderEnabled}
              onCheckedChange={setCartReminderEnabled}
            />
          </div>
          <Label className="font-body text-xs text-foreground/60 font-medium mb-1.5 block">
            Lead time (hours before the window closes)
          </Label>
          <Input
            data-testid="cart-reminder-lead-input"
            type="number"
            min={1}
            max={168}
            value={cartReminderLeadHours}
            onChange={(e) => setCartReminderLeadHours(e.target.value)}
            placeholder="24"
            disabled={!cartReminderEnabled}
            className="rounded-xl bg-muted border-border w-40 mb-4"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border border-border/30">
              <span className="font-body text-xs text-foreground/80">Email channel</span>
              <Switch
                data-testid="cart-reminder-email-toggle"
                checked={cartReminderEmail}
                onCheckedChange={setCartReminderEmail}
                disabled={!cartReminderEnabled}
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border border-border/30">
              <span className="font-body text-xs text-foreground/80">WhatsApp channel</span>
              <Switch
                data-testid="cart-reminder-whatsapp-toggle"
                checked={cartReminderWhatsapp}
                onCheckedChange={setCartReminderWhatsapp}
                disabled={!cartReminderEnabled}
              />
            </div>
          </div>
          <p className="font-body text-[11px] text-foreground/45 mt-2">
            WhatsApp requires an approved Infobip/Meta template (env <code>INFOBIP_WA_TEMPLATE_CART_REMINDER</code>). Until it's approved, the email still sends and WhatsApp is skipped silently. Respects each customer's WhatsApp opt-in.
          </p>
        </div>

        <Button
          onClick={saveReminders}
          disabled={savingReminders}
          data-testid="reminder-save-btn"
          className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm font-bold gap-2"
        >
          {savingReminders ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Reminder Settings
        </Button>
      </div>

      {/* Auto-Cancel Unpaid Orders */}
      <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border" data-testid="auto-cancel-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-xl bg-orange-400/10">
            <Ban className="h-5 w-5 text-orange-400" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-medium text-foreground">Auto-Cancel Unpaid Orders</h2>
            <p className="font-body text-xs text-foreground/60">Cancel unpaid orders after their order window closes</p>
          </div>
        </div>

        <div className="flex items-center justify-between mb-5">
          <div className="pr-4">
            <Label className="font-body text-sm text-foreground font-medium">Enable auto-cancellation</Label>
            <p className="font-body text-xs text-foreground/50 mt-1">
              When ON, orders that are still unpaid once the order window has been closed for the grace period below are automatically cancelled. The customer is emailed (and sent a WhatsApp once that template is approved). Admins can reinstate a cancelled order anytime.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Switch checked={autoCancelEnabled} onCheckedChange={setAutoCancelEnabled} data-testid="toggle-auto-cancel" />
            <span className="font-body text-xs text-foreground/60">{autoCancelEnabled ? 'On' : 'Off'}</span>
          </div>
        </div>

        <div className="max-w-xs mb-5">
          <Label className="font-body text-sm text-foreground font-medium">Grace period after window close (hours)</Label>
          <Input
            type="number"
            min="0"
            data-testid="auto-cancel-grace-input"
            value={autoCancelGrace}
            onChange={e => setAutoCancelGrace(e.target.value)}
            disabled={!autoCancelEnabled}
            className="rounded-xl bg-muted border-border font-body mt-2"
          />
          <p className="font-body text-xs text-foreground/50 mt-2">Default 2 hours. Orders are only cancelled this long after the window closes.</p>
        </div>

        <Button
          onClick={async () => {
            setSavingAutoCancel(true);
            try {
              await adminAPI.updateSettings({
                auto_cancel_unpaid_enabled: autoCancelEnabled,
                auto_cancel_grace_hours: Math.max(0, parseInt(autoCancelGrace, 10) || 0),
              });
              toast.success('Auto-cancel settings saved');
            } catch { toast.error('Failed to save'); }
            finally { setSavingAutoCancel(false); }
          }}
          disabled={savingAutoCancel}
          data-testid="save-auto-cancel-btn"
          className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2"
        >
          {savingAutoCancel ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save Auto-Cancel Settings
        </Button>
      </div>

      {settings && <AutoCloseReadySettings settings={settings} />}

      {/* Banner Management */}
      <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gold/10">
              <Megaphone className="h-5 w-5 text-gold" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-foreground">Announcement Banner</h2>
              <p className="font-body text-xs text-foreground/60">Displayed on the homepage for all visitors</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {bannerVisible ? (
              <Eye className="h-4 w-4 text-gold" />
            ) : (
              <EyeOff className="h-4 w-4 text-foreground/40" />
            )}
            <Switch
              checked={bannerVisible}
              onCheckedChange={async (val) => {
                setBannerVisible(val);
                try {
                  await adminAPI.updateSettings({ banner_visible: val });
                  toast.success(val ? 'Banner shown' : 'Banner hidden');
                } catch { toast.error('Failed to update'); }
              }}
              data-testid="toggle-banner"
            />
            <span className="font-body text-xs text-foreground/60">
              {bannerVisible ? 'Visible' : 'Hidden'}
            </span>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="font-body text-sm text-foreground font-medium">Banner Text</Label>
            <Input
              data-testid="banner-text-input"
              value={bannerText}
              onChange={e => setBannerText(e.target.value)}
              placeholder="e.g. Lowerland Organic Farm and Grumpy Snacks are taking orders for April!"
              className="rounded-xl bg-card border-border font-body mt-2"
            />
            <p className="font-body text-xs text-foreground/50 mt-2">
              Use this to announce which suppliers are taking orders for the next month.
            </p>
          </div>
          <Button
            onClick={async () => {
              setSavingBanner(true);
              try {
                await adminAPI.updateSettings({ banner_text: bannerText });
                toast.success('Banner text saved');
              } catch { toast.error('Failed to save'); }
              finally { setSavingBanner(false); }
            }}
            disabled={savingBanner}
            data-testid="save-banner-btn"
            className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2"
          >
            {savingBanner ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Banner
          </Button>

          {/* Order window status bar toggle */}
          <div className="flex items-center justify-between pt-5 mt-1 border-t border-border">
            <div className="pr-4">
              <Label className="font-body text-sm text-foreground font-medium">Order Window Status Bar</Label>
              <p className="font-body text-xs text-foreground/50 mt-1">
                The dark/red strip at the very top of every page showing how many vendors are accepting orders. Turn off to hide it sitewide.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {windowBarVisible ? (
                <Eye className="h-4 w-4 text-gold" />
              ) : (
                <EyeOff className="h-4 w-4 text-foreground/40" />
              )}
              <Switch
                checked={windowBarVisible}
                onCheckedChange={async (val) => {
                  setWindowBarVisible(val);
                  try {
                    await adminAPI.updateSettings({ window_bar_visible: val });
                    toast.success(val ? 'Status bar shown' : 'Status bar hidden');
                  } catch { toast.error('Failed to update'); }
                }}
                data-testid="toggle-window-bar"
              />
              <span className="font-body text-xs text-foreground/60">
                {windowBarVisible ? 'Visible' : 'Hidden'}
              </span>
            </div>
          </div>

        </div>
      </div>

      {/* Social & Contact */}
      <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border" data-testid="social-contact-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-xl bg-teal/10">
            <MessageCircle className="h-5 w-5 text-teal" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-medium text-foreground">Social &amp; Contact</h2>
            <p className="font-body text-xs text-foreground/60">Instagram link in the footer and a click-to-chat WhatsApp button</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Instagram */}
          <div>
            <Label className="font-body text-sm text-foreground font-medium flex items-center gap-2">
              <Instagram className="h-4 w-4 text-teal" /> Instagram Profile URL
            </Label>
            <Input
              data-testid="instagram-url-input"
              value={social.instagram_url}
              onChange={e => setSocial(f => ({ ...f, instagram_url: e.target.value }))}
              placeholder="https://www.instagram.com/your.profile"
              className="rounded-xl bg-muted border-border font-body mt-2"
            />
            <p className="font-body text-xs text-foreground/50 mt-2">
              Shown as a &quot;Follow us on Instagram&quot; link in the site footer. Leave blank to hide.
            </p>
          </div>

          {/* WhatsApp click-to-chat */}
          <div className="pt-5 border-t border-border">
            <div className="flex items-center justify-between">
              <div className="pr-4">
                <Label className="font-body text-sm text-foreground font-medium">WhatsApp Contact Button</Label>
                <p className="font-body text-xs text-foreground/50 mt-1">
                  A floating &quot;Chat with us&quot; button on every customer page that opens WhatsApp. This is a separate click-to-chat number and does not affect your automated WhatsApp notifications.
                </p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <Switch
                  checked={waContact.enabled}
                  onCheckedChange={val => setWaContact(f => ({ ...f, enabled: val }))}
                  data-testid="toggle-whatsapp-contact"
                />
                <span className="font-body text-xs text-foreground/60">{waContact.enabled ? 'On' : 'Off'}</span>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div>
                <Label className="font-body text-sm text-foreground font-medium">WhatsApp Number</Label>
                <Input
                  data-testid="whatsapp-contact-number-input"
                  value={waContact.number}
                  onChange={e => setWaContact(f => ({ ...f, number: e.target.value }))}
                  placeholder="e.g. 0796901165 or 27796901165"
                  className="rounded-xl bg-muted border-border font-body mt-2"
                />
                <p className="font-body text-xs text-foreground/50 mt-2">
                  SA numbers may start with 0 — we convert to +27 automatically.
                </p>
              </div>
              <div>
                <Label className="font-body text-sm text-foreground font-medium">Pre-filled Message (optional)</Label>
                <Input
                  data-testid="whatsapp-contact-message-input"
                  value={waContact.message}
                  onChange={e => setWaContact(f => ({ ...f, message: e.target.value }))}
                  placeholder="Hi AVZ Wholesale, I have a question about..."
                  className="rounded-xl bg-muted border-border font-body mt-2"
                />
                <p className="font-body text-xs text-foreground/50 mt-2">
                  Text pre-loaded into the customer&apos;s chat when they tap the button.
                </p>
              </div>
            </div>
          </div>

          <Button
            onClick={async () => {
              setSavingSocial(true);
              try {
                await adminAPI.updateSettings({
                  instagram_url: social.instagram_url,
                  whatsapp_contact_enabled: waContact.enabled,
                  whatsapp_contact_number: waContact.number,
                  whatsapp_contact_message: waContact.message,
                });
                toast.success('Social & contact settings saved');
              } catch { toast.error('Failed to save'); }
              finally { setSavingSocial(false); }
            }}
            disabled={savingSocial}
            data-testid="save-social-btn"
            className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2"
          >
            {savingSocial ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Social &amp; Contact
          </Button>
        </div>
      </div>

      {/* Featured Section Management */}
      <div className="p-8 rounded-2xl bg-card border border-border">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-terracotta/10">
              <Star className="h-5 w-5 text-terracotta" />
            </div>
            <div>
              <h2 className="font-heading text-xl font-medium text-foreground">Products Our Customers Love</h2>
              <p className="font-body text-xs text-foreground/60">Featured section on the homepage</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {featuredVisible ? (
              <Eye className="h-4 w-4 text-teal" />
            ) : (
              <EyeOff className="h-4 w-4 text-foreground/40" />
            )}
            <Switch
              checked={featuredVisible}
              onCheckedChange={handleToggleFeaturedVisibility}
              data-testid="toggle-featured-section"
            />
            <span className="font-body text-xs text-foreground/60">
              {featuredVisible ? 'Visible' : 'Hidden'}
            </span>
          </div>
        </div>

        <p className="font-body text-sm text-foreground/70 mb-4">
          Select products to feature and upload promotional images for them. ({featuredIds.length} selected)
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[500px] overflow-y-auto">
          {allProducts.map(product => {
            const isFeatured = featuredIds.includes(product.id);
            return (
              <div
                key={product.id}
                className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${
                  isFeatured ? 'bg-terracotta/5 border-terracotta/30' : 'bg-muted/30 border-border/50'
                }`}
                data-testid={`featured-product-${product.id}`}
              >
                {/* Product Image / Upload */}
                <div className="relative w-14 h-14 rounded-lg overflow-hidden bg-card flex-shrink-0 border border-border">
                  {product.image_url ? (
                    <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-foreground/20">
                      <Upload className="h-4 w-4" />
                    </div>
                  )}
                  <label className="absolute inset-0 flex items-center justify-center bg-black/0 hover:bg-black/30 transition-colors cursor-pointer group">
                    <Upload className="h-3 w-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => e.target.files?.[0] && handleImageUpload(product.id, e.target.files[0])}
                    />
                  </label>
                  {uploading === product.id && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <Loader2 className="h-4 w-4 text-white animate-spin" />
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div className="flex-1 min-w-0">
                  <p className="font-body text-sm font-medium text-foreground truncate">{product.name}</p>
                  <p className="font-body text-[10px] text-foreground/50">{product.supplier_name} — {product.sku}</p>
                </div>

                {/* Toggle */}
                <Button
                  variant={isFeatured ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleToggleFeaturedProduct(product.id)}
                  className={`rounded-full text-xs font-body h-7 px-3 ${
                    isFeatured
                      ? 'bg-terracotta text-white hover:bg-terracotta/90'
                      : 'border-border text-foreground/60 hover:bg-muted'
                  }`}
                  data-testid={`toggle-featured-${product.id}`}
                >
                  {isFeatured ? 'Featured' : 'Feature'}
                </Button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Invoice Settings */}
      <div className="p-6 sm:p-8 rounded-2xl bg-card border border-border">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2 rounded-xl bg-teal/10">
            <FileText className="h-5 w-5 text-teal" />
          </div>
          <div>
            <h2 className="font-heading text-xl font-medium text-foreground">Invoice & Business Details</h2>
            <p className="font-body text-xs text-foreground/60">Appears on customer invoices (PDF)</p>
          </div>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-body text-sm text-foreground font-medium">Company Name</Label>
              <Input data-testid="inv-company" value={invoice.company_name} onChange={e => setInvoice(f => ({...f, company_name: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" placeholder="AVZ Wholesale" />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground font-medium">Registration Number</Label>
              <Input data-testid="inv-reg" value={invoice.registration_number} onChange={e => setInvoice(f => ({...f, registration_number: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="font-body text-sm text-foreground font-medium">VAT Number</Label>
              <Input data-testid="inv-vat" value={invoice.vat_number} onChange={e => setInvoice(f => ({...f, vat_number: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground font-medium">Phone</Label>
              <Input data-testid="inv-phone" value={invoice.phone} onChange={e => setInvoice(f => ({...f, phone: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" />
            </div>
          </div>
          <div>
            <Label className="font-body text-sm text-foreground font-medium">Email</Label>
            <Input data-testid="inv-email" value={invoice.email} onChange={e => setInvoice(f => ({...f, email: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" placeholder="info@avzwholesale.co.za" />
          </div>
          <div>
            <Label className="font-body text-sm text-foreground font-medium">Address Line 1</Label>
            <Input data-testid="inv-address1" value={invoice.address_line1} onChange={e => setInvoice(f => ({...f, address_line1: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" placeholder="Unit 4, 123 Main Road" />
          </div>
          <div>
            <Label className="font-body text-sm text-foreground font-medium">Address Line 2</Label>
            <Input data-testid="inv-address2" value={invoice.address_line2} onChange={e => setInvoice(f => ({...f, address_line2: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" placeholder="Business Park (optional)" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="font-body text-sm text-foreground font-medium">Suburb</Label>
              <Input data-testid="inv-suburb" value={invoice.suburb} onChange={e => setInvoice(f => ({...f, suburb: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" placeholder="Claremont" />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground font-medium">City</Label>
              <Input data-testid="inv-city" value={invoice.city} onChange={e => setInvoice(f => ({...f, city: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" placeholder="Cape Town" />
            </div>
            <div>
              <Label className="font-body text-sm text-foreground font-medium">Postal Code</Label>
              <Input data-testid="inv-postal" value={invoice.postal_code} onChange={e => setInvoice(f => ({...f, postal_code: e.target.value}))} className="rounded-xl bg-muted border-border mt-1.5" placeholder="7708" />
            </div>
          </div>
          <p className="font-body text-xs text-foreground/40">Used in full on invoices &amp; purchase orders. The website footer shows only the <span className="text-teal">Suburb</span>.</p>
          <div>
            <Label className="font-body text-sm text-foreground font-medium">Banking Details</Label>
            <textarea
              data-testid="inv-bank"
              value={invoice.bank_details}
              onChange={e => setInvoice(f => ({...f, bank_details: e.target.value}))}
              className="w-full rounded-xl bg-muted border border-border font-body text-sm text-foreground mt-1.5 p-3 min-h-[80px] resize-none focus:border-teal focus:ring-1 focus:ring-teal/20"
              placeholder={"Bank: FNB\nAccount: 1234567890\nBranch: 250655"}
            />
          </div>
          <Button
            onClick={async () => {
              setSavingInvoice(true);
              try {
                await adminAPI.updateSettings({
                  invoice_company_name: invoice.company_name,
                  invoice_registration_number: invoice.registration_number,
                  invoice_vat_number: invoice.vat_number,
                  invoice_address: invoice.address,
                  invoice_address_line1: invoice.address_line1,
                  invoice_address_line2: invoice.address_line2,
                  invoice_suburb: invoice.suburb,
                  invoice_city: invoice.city,
                  invoice_postal_code: invoice.postal_code,
                  invoice_bank_details: invoice.bank_details,
                  invoice_phone: invoice.phone,
                  invoice_email: invoice.email,
                });
                toast.success('Invoice settings saved');
              } catch { toast.error('Failed to save'); }
              finally { setSavingInvoice(false); }
            }}
            disabled={savingInvoice}
            data-testid="save-invoice-btn"
            className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm gap-2"
          >
            {savingInvoice ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Invoice Settings
          </Button>
        </div>
      </div>

      <DangerZone />
    </div>
  );
}
